/**
 * AdminSourcesPage.js — /admin/sources
 *
 * RC 1.25.K_BIG (Part C) — Admin Freshness Card.
 *
 * Minimal operator surface that reads GET /api/admin/sources/freshness
 * (require_admin on the backend) and renders one row per configured
 * `job_sources` record so operators can eyeball source health without
 * opening a SQL shell.
 *
 * Access model:
 *   - Route is wired in App.js behind ProtectedRoute (auth + profile).
 *   - Fine-grained gating lives on the backend: non-admin callers
 *     get a 403, which this page surfaces as a plain "Admin only"
 *     message. No sidebar nav entry — hidden entirely for non-admins.
 *
 * Out-of-scope for this slice (honoured):
 *   - No auto-refresh (manual refresh button only).
 *   - No candidate workflow changes.
 *   - No changes to Automation semantics, CREDITS_ENFORCED, or any
 *     other accepted-slice behaviour.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, RefreshCw, ShieldAlert, Play, CheckCircle2, AlertCircle, Info } from 'lucide-react';

/** Truncate long last_error strings so the table stays readable. */
function truncate(value, max = 120) {
  if (!value) return '';
  const s = String(value);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Render a timestamp as locale date+time (short) or em-dash if empty. */
function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: '2-digit', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Subtle colour cue for latest_task_status. Not load-bearing. */
function statusTone(status) {
  switch (status) {
    case 'succeeded': return 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40';
    case 'failed':    return 'bg-rose-500/10 text-rose-200 border-rose-500/40';
    case 'running':   return 'bg-sky-500/10 text-sky-200 border-sky-500/40';
    case 'pending':   return 'bg-ndor-surface-alt text-slate-200 border-slate-200';
    default:          return 'bg-ndor-surface-alt text-slate-300 border-slate-200';
  }
}

export default function AdminSourcesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  // RC 1.26.B.1.1 — per-row Run Now state. Keyed by source id.
  // Values: {status: 'running'|'enqueued'|'already_running'|'error',
  //          message?: string}
  const [runState, setRunState] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const data = await apiFetch('/api/admin/sources/freshness');
      setItems(Array.isArray(data?.items) ? data.items : []);
      setFetchedAt(new Date());
    } catch (err) {
      if (err?.status === 403) {
        setForbidden(true);
      } else {
        setError(err?.message || 'Failed to load source freshness.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** RC 1.26.B.1.1 — POST /api/admin/sources/{id}/run_now.
   *
   * The endpoint returns either:
   *   {ok:true, status:"enqueued",         task:{…}}  — new task
   *   {ok:true, status:"already_running",  task:{…}}  — duplicate-
   *                                                    task guard
   * or raises (e.g. 404 if source was deleted underneath us). We
   * surface each outcome inline without triggering a full table
   * refresh — the operator can click Refresh to see the updated
   * `latest_task_status` once the worker claims the task.
   */
  async function runNow(row) {
    const id = row.id;
    setRunState((s) => ({ ...s, [id]: { status: 'running' } }));
    try {
      const resp = await apiFetch(
        `/api/admin/sources/${id}/run_now`,
        { method: 'POST' },
      );
      setRunState((s) => ({
        ...s,
        [id]: { status: resp?.status || 'enqueued' },
      }));
    } catch (err) {
      const detail = err?.data?.detail;
      setRunState((s) => ({
        ...s,
        [id]: {
          status: 'error',
          message: (typeof detail === 'string' && detail)
                   || err?.message
                   || 'Run failed',
        },
      }));
    }
  }

  if (forbidden) {
    return (
      <div
        data-testid="admin-sources-forbidden"
        className="mx-auto max-w-xl py-16 px-4 text-center"
      >
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-semibold text-ndor-ink">Admin only</h1>
        <p className="mt-2 text-sm text-slate-300">
          You need operator access to view source freshness.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-sources-page"
      className="mx-auto max-w-[1400px] px-4 py-8 space-y-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ndor-ink">
            Source Freshness
          </h1>
          <p className="mt-1 text-sm text-slate-300">
            Operator view of configured ingestion sources. Health is
            maintained by the ingestion worker on every run.
          </p>
          {fetchedAt && (
            <p
              data-testid="admin-sources-fetched-at"
              className="mt-1 text-xs text-slate-300"
            >
              Fetched {fetchedAt.toLocaleString()}
            </p>
          )}
        </div>
        <Button
          data-testid="admin-sources-refresh-btn"
          variant="outline"
          onClick={load}
          disabled={loading}
        >
          {loading
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div
          data-testid="admin-sources-error"
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
        >
          {error}
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-ndor-surface overflow-x-auto">
        <Table data-testid="admin-sources-table">
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Cadence</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead>Last OK</TableHead>
              <TableHead className="text-right">Fails</TableHead>
              <TableHead className="text-right">Active</TableHead>
              <TableHead className="text-right">Stale</TableHead>
              <TableHead className="text-right">404</TableHead>
              <TableHead>Latest task</TableHead>
              <TableHead>Last error</TableHead>
              <TableHead className="text-right">Run</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={13}
                  className="py-10 text-center text-sm text-slate-300"
                  data-testid="admin-sources-loading"
                >
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading source freshness…
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && !error && (
              <TableRow>
                <TableCell
                  colSpan={13}
                  className="py-10 text-center text-sm text-slate-300"
                  data-testid="admin-sources-empty"
                >
                  No ingestion sources configured.
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow
                key={row.id}
                data-testid={`admin-sources-row-${row.id}`}
              >
                <TableCell className="font-medium text-ndor-ink">
                  {row.name || '—'}
                </TableCell>
                <TableCell className="text-slate-300">
                  {row.source_type || '—'}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      row.enabled
                        ? 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40'
                        : 'bg-ndor-surface-alt text-slate-300 border-slate-200'
                    }
                  >
                    {row.enabled ? 'on' : 'off'}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-300">
                  {row.cadence || '—'}
                </TableCell>
                <TableCell className="text-slate-300 whitespace-nowrap">
                  {fmtTs(row.last_run_at)}
                </TableCell>
                <TableCell className="text-slate-300 whitespace-nowrap">
                  {fmtTs(row.last_ok_at)}
                </TableCell>
                <TableCell
                  className={`text-right tabular-nums ${
                    (row.consecutive_failures || 0) > 0
                      ? 'text-rose-200 font-medium' : 'text-slate-300'
                  }`}
                >
                  {row.consecutive_failures || 0}
                </TableCell>
                <TableCell className="text-right tabular-nums text-slate-200">
                  {row.active_jobs ?? 0}
                </TableCell>
                <TableCell className="text-right tabular-nums text-slate-300">
                  {row.hidden_stale ?? 0}
                </TableCell>
                <TableCell className="text-right tabular-nums text-slate-300">
                  {row.hidden_404 ?? 0}
                </TableCell>
                <TableCell>
                  {row.latest_task_status
                    ? (
                      <Badge
                        variant="outline"
                        className={statusTone(row.latest_task_status)}
                      >
                        {row.latest_task_status}
                      </Badge>
                    )
                    : <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell
                  className="text-xs text-rose-200 max-w-[280px]"
                  title={row.last_error || ''}
                >
                  {truncate(row.last_error) || <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {(() => {
                    const state = runState[row.id];
                    const busy = state?.status === 'running';
                    return (
                      <div className="flex items-center justify-end gap-2">
                        {state?.status === 'enqueued' && (
                          <span
                            data-testid={`admin-sources-run-enqueued-${row.id}`}
                            className="inline-flex items-center gap-1 text-xs text-emerald-200"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Enqueued
                          </span>
                        )}
                        {state?.status === 'already_running' && (
                          <span
                            data-testid={`admin-sources-run-already-${row.id}`}
                            className="inline-flex items-center gap-1 text-xs text-slate-300"
                          >
                            <Info className="h-3.5 w-3.5" /> Already running
                          </span>
                        )}
                        {state?.status === 'error' && (
                          <span
                            data-testid={`admin-sources-run-error-${row.id}`}
                            title={state.message}
                            className="inline-flex items-center gap-1 text-xs text-rose-200 max-w-[200px] truncate"
                          >
                            <AlertCircle className="h-3.5 w-3.5" /> {state.message}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant={state?.status === 'enqueued' || state?.status === 'already_running' ? 'outline' : 'default'}
                          onClick={() => runNow(row)}
                          disabled={busy || !row.enabled}
                          data-testid={`admin-sources-run-btn-${row.id}`}
                          title={
                            !row.enabled
                              ? 'Source is disabled'
                              : 'Enqueue a manual ingestion run'
                          }
                        >
                          {busy
                            ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            : <Play className="mr-1 h-3.5 w-3.5" />}
                          Run now
                        </Button>
                      </div>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
